// Shared Zod building blocks — imported by BOTH sides of the wire (fe forms and
// api validators), so everything here must stay browser-safe. Generation lives in
// @erp/platform (worker-only); these are the *shapes*.
import { z } from "zod";

/** ULID: 26 Crockford-base32 chars, lexicographically time-sortable (ADR-000 §9.2). First char capped at 7 — the 48-bit timestamp bound. */
export const Ulid = z.string().regex(/^[0-7][0-9A-HJKMNP-TV-Z]{25}$/, "ulid");
export type Ulid = z.infer<typeof Ulid>;

/**
 * Opaque entity id. Ported data keeps legacy shapes forever (UUIDs, MBSPSU's
 * APP-/PAY-/REC- prefixes — ADR-003 §9): cross-module references validate as
 * opaque strings, never as Ulid. New rows generate ULIDs beside old ids.
 */
export const EntityId = z.string().min(1).max(64);
export type EntityId = z.infer<typeof EntityId>;

/** Money is integer paise — never floats, never floating rupees (ADR-000 §9.2). Non-negative: prices, fees, balances-by-construction. */
export const Paise = z.number().int().min(0).brand<"Paise">();
export type Paise = z.infer<typeof Paise>;

/** Signed paise — refunds, adjustments, credit notes, ledger deltas. Use deliberately; `Paise` stays the default. */
export const SignedPaise = z.number().int().brand<"SignedPaise">();
export type SignedPaise = z.infer<typeof SignedPaise>;

/** Epoch milliseconds — the one clock unit (kernel `now()` convention). */
export const EpochMs = z.number().int().nonnegative();
export type EpochMs = z.infer<typeof EpochMs>;

/**
 * Period axes (ADR-000 §2 partition law — the suffix encodes the calendar):
 *   AcademicYear "2026"  = AY 2026-27 (≈1 Jul–30 Jun) → DB_*_Y2026
 *   AdmissionCycle "C2026"                            → DB_ADM_C2026
 *   FiscalYear "FY2627"  = 1 Apr 2026–31 Mar 2027    → DB_FIN_FY2627
 */
export const AcademicYear = z.string().regex(/^\d{4}$/, "academic year, e.g. 2026");
export type AcademicYear = z.infer<typeof AcademicYear>;

export const AdmissionCycle = z.string().regex(/^C\d{4}$/, "admission cycle, e.g. C2026");
export type AdmissionCycle = z.infer<typeof AdmissionCycle>;

export const FiscalYear = z.string().regex(/^FY\d{4}$/, "fiscal year, e.g. FY2627");
export type FiscalYear = z.infer<typeof FiscalYear>;
